/**
 * <event-console-dashboard> — the console's default destination: the
 * lifecycle-swapped numbers and per-type inventory. The traffic report is its
 * own destination (analytics.view.ts), not a block on this one.
 *
 * A routed component in the console's own nested `event-console` area, not a
 * template branch of the shell. That is what keeps the chosen destination in
 * the URL and out of the shell's local field, so navigating between
 * destinations (or reloading on one) no longer resets it.
 *
 * Every branch below establishes its own scroll region. The area that mounts a
 * destination stretches it to exactly the area's own box and pins its minimum
 * height to zero, so a destination taller than that box paints the excess past
 * its own border box as ink no gesture in the subtree reaches — the header
 * above stays pinned because it is the area's sibling, and the body scrolls
 * because this view, not the area, is what bounds it.
 *
 * It reads the event from `consoleViewModel` rather than taking one as a
 * property: the area constructs a routed component with no bindings, and this
 * view model is already the single derivation every destination shares.
 */

import { formatCurrency, formatNumber } from "@mhmo91/rx";
import { area } from "@mhmo91/schmancy";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { canViewSaleReports } from "../../admin/sale/sale.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { canManageEvents } from "../events.state.ts";
import type { ConsoleViewModel } from "./console.state.ts";
import {
	CURRENCY,
	consoleDestination,
	consoleEvent,
	consoleEventId,
	consoleViewModel,
} from "./console.state.ts";
import "../ticket-types/ticket-type-form.view.ts";
import "../programme-calendar.view.ts";
import "./revenue-breakdown.view.ts";

@schmancyElement("event-console-dashboard")
export default class EventConsoleDashboard extends LocalizeMixin(
	SchmancyElement,
) {
	private openTicketType(typeId: string): void {
		const eventId = consoleEventId.value;
		if (!eventId) return;
		show(
			() =>
				html`<ticket-type-form .eventId=${eventId} .id=${typeId}></ticket-type-form>`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	private goToEdit(): void {
		area.push({
			...consoleDestination("event-console-edit"),
			historyStrategy: "replace",
		});
	}

	/** The one money cell both lifecycle grids render — the fee-report ladder
	 *  the view model's doc comments define. While no PSP order has a fee
	 *  report the headline is the gross under its own honest label; with every
	 *  report in (or no PSP leg at all) it is the exact you-keep figure; in
	 *  between it is an upper bound said in those words, because fees only
	 *  grow as reports land. The cell is the affordance into the breakdown
	 *  overlay only once a paid order exists — before that there is nothing to
	 *  work out, so the figure stays a static reading. */
	private renderMoneyCell(vm: ConsoleViewModel): TemplateResult {
		const pending = vm.youKeepCents === null;
		const partial =
			!pending &&
			vm.feeOrdersTotal > 0 &&
			vm.feeOrdersReported < vm.feeOrdersTotal;
		const label = this.t(
			pending
				? "admin.events.console.grossTakenLabel"
				: "admin.events.console.youKeepLabel",
		);
		const figure = pending
			? formatCurrency(vm.grossTakenCents / 100, CURRENCY, this.locale)
			: partial
				? this.t("admin.events.console.youKeepAtMost", {
						amount: formatCurrency(
							vm.youKeepCents / 100,
							CURRENCY,
							this.locale,
						),
					})
				: formatCurrency(vm.youKeepCents / 100, CURRENCY, this.locale);
		const vat = formatCurrency(vm.vatOwedCents / 100, CURRENCY, this.locale);
		const support = pending
			? this.t("admin.events.console.grossTakenIncludes", { vat })
			: partial
				? this.t("admin.events.console.youKeepAfterAtLeast", {
						vat,
						reported: vm.feeOrdersReported,
						total: vm.feeOrdersTotal,
					})
				: this.t("admin.events.console.youKeepAfter", {
						vat,
						fees: formatCurrency(
							vm.feeReportedCents / 100,
							CURRENCY,
							this.locale,
						),
					});
		const breakdownAria = this.t(
			pending
				? "admin.events.console.grossTakenBreakdownAria"
				: "admin.events.console.youKeepBreakdownAria",
		);
		return html`
			<div class="flex flex-col gap-1">
				${when(
					vm.orders.some((o) => o.status === "paid"),
					() => html`
						<!-- The one interactive cell among three that look alike, so the
						     affordance is carried by cursor, hover and pressed treatment
						     rather than by a 16px chevron a touch operator never hovers.
						     The tint bleeds past the text on every side; -m-2 cancels p-2
						     exactly, so the cell keeps the box its two siblings align to. -->
						<button
							type="button"
							class="-m-2 flex cursor-pointer touch-manipulation flex-col gap-1 rounded-lg p-2 text-left transition-colors duration-150 hover:bg-surface-container active:bg-surface-containerHigh"
							aria-label=${breakdownAria}
							@click=${() =>
								show(() => html`<revenue-breakdown></revenue-breakdown>`, {
									ariaLabel: this.t(
										"admin.events.console.revenueBreakdown.dialogAriaLabel",
									),
								})
									.pipe(takeUntil(this.disconnecting))
									.subscribe()}
						>
							<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${label}</schmancy-typography>
							<span class="flex items-center gap-1">
								<schmancy-typography type="title" token="lg" weight="bold" class="tabular-nums">${figure}</schmancy-typography>
								<schmancy-icon class="text-surface-onVariant" aria-hidden="true">chevron_right</schmancy-icon>
							</span>
							<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${support}</schmancy-typography>
						</button>
					`,
					() => html`
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${label}</schmancy-typography>
						<schmancy-typography type="title" token="lg" weight="bold" class="tabular-nums">${figure}</schmancy-typography>
						<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${support}</schmancy-typography>
					`,
				)}
				${when(
					vm.authorisedAmount > 0,
					() =>
						html`<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${this.t("admin.events.console.authorisedSuffix", { amount: formatCurrency(vm.authorisedAmount / 100, CURRENCY, this.locale) })}</schmancy-typography>`,
				)}
			</div>
		`;
	}

	override render(): TemplateResult {
		const vm = consoleViewModel.get();

		if (!vm) {
			return html`
				<div role="status" aria-busy="true" ${overflowWithin()} class="flex h-full flex-col gap-3 p-4 md:p-6">
					<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
					<schmancy-skeleton class="h-32 w-full rounded-2xl"></schmancy-skeleton>
				</div>
			`;
		}

		if (!canViewSaleReports.get()) {
			return html`
				<div ${overflowWithin()} class="flex h-full flex-col items-start gap-2 px-4 py-8 md:px-6">
					<schmancy-icon class="text-3xl text-surface-onVariant">lock</schmancy-icon>
					<schmancy-typography type="body" token="sm" class="max-w-sm text-surface-onVariant">
						${this.t("admin.events.console.noAccessBody")}
					</schmancy-typography>
				</div>
			`;
		}

		if (!vm.hasTicketTypes) {
			return html`
				<div ${overflowWithin()} class="flex h-full flex-col items-start gap-3 px-4 py-8 md:px-6">
					<schmancy-icon class="text-3xl text-surface-onVariant">confirmation_number</schmancy-icon>
					<schmancy-typography type="title" token="sm">${this.t("admin.events.console.noTicketTypesTitle")}</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="max-w-sm text-surface-onVariant">
						${this.t("admin.events.console.noTicketTypesBody")}
					</schmancy-typography>
					${when(
						canManageEvents.get(),
						() => html`
							<schmancy-button variant="filled" @click=${() => this.goToEdit()}>
								${this.t("admin.events.console.goToEditButton")}
							</schmancy-button>
						`,
					)}
				</div>
			`;
		}

		return html`
			<div ${overflowWithin()} class="flex h-full flex-col gap-4 p-4 md:p-6">
				${when(
					vm.publicShopEmpty,
					() => html`
						<div role="status" class="flex flex-col gap-1">
							<schmancy-typography type="title" token="sm" class="text-warning-default">
								${this.t("admin.events.console.publicShopEmptyTitle")}
							</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="max-w-prose text-surface-onVariant">
								${this.t("admin.events.console.publicShopEmptyBody")}
							</schmancy-typography>
						</div>
					`,
				)}
				${when(
					vm.lifecycle === "onSale",
					() => html`
						<schmancy-surface type="containerLow" class="grid grid-cols-1 gap-4 rounded-xl p-4 sm:grid-cols-3">
							${this.renderMoneyCell(vm)}
							<div class="flex flex-col gap-1">
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.last7dLabel")}</schmancy-typography>
								<schmancy-typography type="title" token="lg" weight="bold" class="tabular-nums">${formatNumber(vm.last7d, this.locale)}</schmancy-typography>
								<div class="flex items-center gap-1">
									<schmancy-icon
										class=${this.classMap({ "text-sm": true, "text-success-default": vm.last7d > vm.prev7d, "text-surface-onVariant": vm.last7d <= vm.prev7d })}
									>
										${vm.last7d > vm.prev7d ? "trending_up" : vm.last7d < vm.prev7d ? "trending_down" : "trending_flat"}
									</schmancy-icon>
									<schmancy-typography
										type="body"
										token="xs"
										class=${this.classMap({ "text-success-default": vm.last7d > vm.prev7d, "text-surface-onVariant": vm.last7d <= vm.prev7d })}
									>
										${
											vm.last7d > vm.prev7d
												? this.t("admin.events.console.momentumUp", {
														count: vm.last7d - vm.prev7d,
													})
												: vm.last7d < vm.prev7d
													? this.t("admin.events.console.momentumDown", {
															count: vm.prev7d - vm.last7d,
														})
													: this.t("admin.events.console.momentumFlat")
										}
									</schmancy-typography>
								</div>
							</div>
							<div class="flex flex-col gap-1">
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.refundedCancelledLabel")}</schmancy-typography>
								<schmancy-typography type="title" token="lg" weight="bold" class="tabular-nums">${formatNumber(vm.refundedCancelledCount, this.locale)}</schmancy-typography>
							</div>
						</schmancy-surface>
					`,
					() => html`
						<schmancy-surface type="containerLow" class="grid grid-cols-1 gap-4 rounded-xl p-4 sm:grid-cols-2">
							<div class="flex flex-col gap-1">
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
									${
										vm.lifecycle === "present"
											? this.t("admin.events.console.presentLabel")
											: this.t("admin.events.console.attendanceLabel")
									}
								</schmancy-typography>
								${when(
									vm.attendanceKnown,
									() => html`
										<schmancy-typography type="title" token="lg" weight="bold" class="tabular-nums">
											${this.t("admin.events.console.checkedInFraction", { redeemed: vm.redeemedTickets, valid: vm.validTickets })}
										</schmancy-typography>
										${when(
											vm.lifecycle === "present",
											() =>
												html`<schmancy-typography type="body" token="xs" class="text-surface-onVariant">${this.t("admin.events.console.arrivals15", { count: vm.arrivalsLast15Min })}</schmancy-typography>`,
										)}
									`,
									() =>
										html`<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.noCheckInsYet")}</schmancy-typography>`,
								)}
							</div>
							${this.renderMoneyCell(vm)}
						</schmancy-surface>
					`,
				)}

				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.t("admin.events.console.typesTitle")}</schmancy-typography>
					<!-- Every programme day at once — the operator is accounting for
					     the whole programme, never standing on one day — and, on an
					     event with no day dimension at all, the same lines ungrouped.
					     One surface either way: the row is the instrument and the
					     grouping above it is what varies. -->
					<programme-calendar
						.rows=${vm.typeRows}
						.event=${consoleEvent.get()}
						@open=${(e: CustomEvent<{ id: string }>) =>
							this.openTicketType(e.detail.id)}
					></programme-calendar>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-console-dashboard": EventConsoleDashboard;
	}
}
